import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  Box,
  Typography,
  DialogContent,
  TextField,
  Button,
  DialogActions,
  Grid,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CancelOutlinedIcon from "@material-ui/icons/CancelOutlined";
import { QrBarcodeScanner } from "../../../components/common/QrBarcodeScanner/QrBarcodeScanner";
import { useNotify } from "react-admin";
import { beep } from "./../../../utils/utils";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
  },
  closeButton: {
    cursor: "pointer",
  },
  primaryColor: {
    color: theme.palette.primary.main,
  },
  deleteButton: {
    color: theme.palette.error.main,
  },
}));

const ConfirmDialog = ({
  open,
  onClose,
  saving,
  barcode,
  seals,
  onConfirm,
}) => {
  const styles = useStyles();
  const toBeBrokenSeal = seals.find((s) => s.barcode === barcode);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogContent>
        {!toBeBrokenSeal && (
          <Typography>
            This Barcode <Typography variant="overline">"{barcode}"</Typography>{" "}
            is not in the list! You can not break it.
          </Typography>
        )}
        {toBeBrokenSeal && (
          <Typography>
            Are you sure you want to break the seal with barcode{" "}
            <Typography variant="overline">"{barcode}"</Typography> ?
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        {toBeBrokenSeal && (
          <Button
            onClick={() => onConfirm(toBeBrokenSeal)}
            className={styles.deleteButton}
            disabled={saving}
            color="primary"
          >
            {saving ? <CircularProgress size={24} /> : "Break"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export const BreakSealsDialog = ({
  onClose,
  inboundShipment,
  dispatch,
  loading,
  breakSealRequest,
}) => {
  const brokenBy = window.appUser.fullName;
  const seals =
    inboundShipment?.seals?.items.filter(
      (s) => !s.brokenAt && !s.brokenBy && s.status !== "new"
    ) || [];
  const [barcode, setBarcode] = useState("");
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [sealError, setSealError] = useState("");
  const [isScannerOpen, setIsScannerOpen] = useState(true);
  const ref = useRef();
  const breakInFlightRef = useRef(false);
  const notify = useNotify();
  const styles = useStyles();

  useEffect(() => {
    const focusTimer = setTimeout(() => {
      ref.current?.focus();
    }, 0);

    return () => clearTimeout(focusTimer);
  }, []);

  const breakSeal = async (seal) => {
    if (loading || breakInFlightRef.current) return;
    if (!barcode) {
      setSealError("Required");
      return;
    }
    breakInFlightRef.current = true;
    try {
      dispatch({ type: "SET_LOADING", payload: true });
      await breakSealRequest({
        notify,
        dispatch,
        inboundShipment,
        createdBy: brokenBy,
        barcode: seal.barcode,
        sealId: seal.id,
      });
    } finally {
      breakInFlightRef.current = false;
      dispatch({ type: "SET_LOADING", payload: false });
      onClose();
    }
  };

  const handleScanSuccess = (v) => {
    if (v !== ref.current?.value) {
      setBarcode(v);
      beep(100, 520, 200);
    }
    setIsScannerOpen(false);
  };

  const handleBarcodeInputChange = (e) => {
    if (e.target.value) {
      setSealError("");
    } else {
      setSealError("Required");
    }
    setBarcode(e.target.value);
  };

  const handleConfirmBreak = async (seal) => {
    setConfirmDialogOpen(false);
    await breakSeal(seal);
  };

  const handleCancelBreak = () => {
    setConfirmDialogOpen(false);
  };

  const onBreakSeal = () => {
    if (loading || breakInFlightRef.current) return;
    if (!barcode) {
      setSealError("Required");
      return;
    }
    setConfirmDialogOpen(true);
  };

  return (
    <>
      <Dialog open={true} fullWidth={true}>
        <Box
          component="span"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          m={2}
        >
          <Typography variant="h6">Break Seal</Typography>
          <CancelOutlinedIcon
            className={styles.closeButton}
            fontSize="large"
            onClick={onClose}
          />
        </Box>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {isScannerOpen ? (
                <QrBarcodeScanner onScanSuccess={handleScanSuccess} />
              ) : (
                <Box display="flex" justifyContent="center" mb={2}>
                  <Button
                    onClick={() => setIsScannerOpen(true)}
                    color="primary"
                    variant="outlined"
                    margin="dense"
                  >
                    Rescan
                  </Button>
                </Box>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Barcode"
                variant="outlined"
                fullWidth
                margin="dense"
                value={barcode}
                onChange={handleBarcodeInputChange}
                error={!!sealError}
                helperText={sealError}
                inputRef={ref}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (loading || breakInFlightRef.current) return;
                    onBreakSeal();
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          {!loading && <Button onClick={onClose}>Close</Button>}
          <Button color="primary" onClick={onBreakSeal} disabled={loading}>
            {loading ? <CircularProgress color="inherit" /> : "Break"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmDialogOpen}
        onClose={handleCancelBreak}
        onConfirm={handleConfirmBreak}
        saving={loading}
        barcode={barcode}
        seals={seals}
      />
    </>
  );
};
